import { PdProductItem } from 'interfaces/line-item';
import { ProductType } from 'interfaces/product';

const MIN_FRACTION_DIGITS = 2;
const MAX_FRACTION_DIGITS = 2;

/**
 * Format price with locale and currency symbol
 */
export const formatPrice = ({
  currency,
  locale,
  price,
}: {
  currency: string;
  price: number;
  locale: string | undefined;
}): string =>
  price.toLocaleString(locale ?? 'de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: MIN_FRACTION_DIGITS,
    maximumFractionDigits: MAX_FRACTION_DIGITS,
  });

/**
 * Product types that are eligible for savings plans
 */
const ELIGIBLE_PRODUCT_TYPES = new Set<ProductType>([
  ProductType.WetFood,
  ProductType.DryFood,
  ProductType.FoodSupplement,
  ProductType.FreshMenus,
  ProductType.Snacks,
]);

/**
 * Check if the product is eligible for savings plan based on product type.
 */
export const isProductEligibleForSavingsPlan = (productType: ProductType | undefined): boolean => {
  if (!productType) return false;

  return ELIGIBLE_PRODUCT_TYPES.has(productType);
};

interface HasEligibleProductsParams {
  items: PdProductItem[] | undefined;
  savingsPlanVariantIds: (string | number)[];
}

/**
 * Checks if cart contains at least one product eligible for savings plan.
 * Excludes savings plan items themselves from the check.
 */
export const hasEligibleProductsForSavingsPlan = ({
  items,
  savingsPlanVariantIds,
}: HasEligibleProductsParams): boolean => {
  if (!items?.length) return false;

  // Filter out savings plan items from the cart
  const productItems = items.filter(
    (item) =>
      !savingsPlanVariantIds.some(
        (planVariantId) => String(planVariantId) === String(item.variantId)
      )
  );

  // Check if any remaining product is eligible for savings plan
  return productItems.some((item) => isProductEligibleForSavingsPlan(item.productType));
};
